import { create } from 'zustand';
import storage from '../native/storage.js';
import {
  DEFAULT_UI_VARIANT,
  isKnownVariant,
  DEFAULT_HOME_LAYOUT,
  isKnownHomeLayout,
} from '../config/ui.config.js';

/**
 * Single shared source of truth for the active UI variant + home-screen
 * layout preference.
 *
 * Why a Zustand store (vs. a plain useStorage hook): every consumer that
 * called `useUIVariant` used to get its own copy of the storage state,
 * so when the picker mutated localStorage, only the picker re-rendered —
 * App.jsx kept rendering the old page set until a manual reload. A shared
 * store fixes the propagation so preference switches take effect
 * immediately across the whole app.
 */

const VARIANT_KEY = 'lexonauts.ui.variant';
const HOME_LAYOUT_KEY = 'lexonauts.ui.homeLayout';

const initialState = {
  hydrated: false,
  variant: DEFAULT_UI_VARIANT,
  homeLayout: DEFAULT_HOME_LAYOUT,
};

export const useUIVariantStore = create((set, get) => ({
  ...initialState,

  /**
   * Read every persisted preference once at app boot. Idempotent.
   */
  async hydrate() {
    if (get().hydrated) return;
    const [storedVariant, storedLayout] = await Promise.all([
      storage.getItem(VARIANT_KEY),
      storage.getItem(HOME_LAYOUT_KEY),
    ]);
    const safeVariant = isKnownVariant(storedVariant)
      ? storedVariant
      : DEFAULT_UI_VARIANT;
    const safeLayout = isKnownHomeLayout(storedLayout)
      ? storedLayout
      : DEFAULT_HOME_LAYOUT;
    set({ variant: safeVariant, homeLayout: safeLayout, hydrated: true });
  },

  /**
   * Update the active variant and persist it. Unknown ids ignored so
   * callers can pass user input directly.
   */
  async setVariant(next) {
    if (!isKnownVariant(next)) return;
    if (next === get().variant) return;
    set({ variant: next });
    await storage.setItem(VARIANT_KEY, next);
  },

  /**
   * Update the home layout preference and persist it.
   */
  async setHomeLayout(next) {
    if (!isKnownHomeLayout(next)) return;
    if (next === get().homeLayout) return;
    set({ homeLayout: next });
    await storage.setItem(HOME_LAYOUT_KEY, next);
  },

  /** Test/dev helper: reset to defaults and clear storage. */
  async reset() {
    set({ ...initialState });
    await Promise.all([
      storage.removeItem(VARIANT_KEY),
      storage.removeItem(HOME_LAYOUT_KEY),
    ]);
  },
}));

export const selectVariant = (s) => s.variant;
export const selectHomeLayout = (s) => s.homeLayout;
export const selectHydrated = (s) => s.hydrated;

/**
 * Convenience for App.jsx boot — mirrors `hydrateProgressStore()`.
 */
export function hydrateUIVariantStore() {
  return useUIVariantStore.getState().hydrate();
}
